/**
 * InteractionsList — uniform "Interacts with" section for supplement
 * and medication detail sheets.
 *
 * One row per interaction: severity dot + name of the other item, then
 * the concern and (optional) recommendation underneath. Severity drives
 * the dot color and the section's intro line.
 *
 *   note     → warm (timing / minor consideration)
 *   caution  → coral (clinical concern, mention to clinic)
 *   critical → coral, stronger language (clinician sign-off required)
 *
 * Caller controls section heading + outer spacing. This component
 * renders only the rows.
 */
import { Pressable, View, Text } from "react-native";
import { EMBER } from "@louise/moon-ui";
import type { Interaction } from "../types";

const SEVERITY_COLOR: Record<Interaction["severity"], string> = {
  note: EMBER.colors.warm,
  caution: EMBER.colors.coral,
  critical: EMBER.colors.coral,
};

const DEFAULT_SEVERITY_LABEL: Record<Interaction["severity"], string> = {
  note: "Note",
  caution: "Caution",
  critical: "Critical",
};

const TAKING_TAG_COLOR = "#6B5548";

type Props = {
  interactions: Interaction[];
  onLinkedBlockPress?: (blockId: string) => void;
  takingLinkedBlockIds?: string[];
  takingLabel?: string;
  severityLabels?: Record<Interaction["severity"], string>;
};

export function InteractionsList({
  interactions,
  onLinkedBlockPress,
  takingLinkedBlockIds = [],
  takingLabel = "Taking",
  severityLabels = DEFAULT_SEVERITY_LABEL,
}: Props) {
  if (interactions.length === 0) return null;

  return (
    <View style={{ gap: 10 }}>
      {interactions.map((interaction, idx) => {
        const color = SEVERITY_COLOR[interaction.severity];
        const isLinked = Boolean(interaction.linkedBlockId);
        const isTakingLinked = interaction.linkedBlockId
          ? takingLinkedBlockIds.includes(interaction.linkedBlockId)
          : false;
        const Row = isLinked && onLinkedBlockPress ? Pressable : View;
        return (
          <Row
            key={`${interaction.with}-${idx}`}
            onPress={
              isLinked && onLinkedBlockPress && interaction.linkedBlockId
                ? () => onLinkedBlockPress(interaction.linkedBlockId!)
                : undefined
            }
            style={{
              gap: 8,
              padding: 12,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.34)",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                  flex: 1,
                }}
              >
                {interaction.with}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  fontWeight: "600",
                  color,
                  textTransform: "uppercase",
                  letterSpacing: 0,
                  paddingHorizontal: 7,
                  paddingVertical: 3,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: color,
                }}
              >
                {severityLabels[interaction.severity]}
              </Text>
              {isTakingLinked ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    fontWeight: "600",
                    color: "#FFFFFF",
                    textTransform: "uppercase",
                    letterSpacing: 0,
                    paddingHorizontal: 7,
                    paddingVertical: 3,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: TAKING_TAG_COLOR,
                    backgroundColor: TAKING_TAG_COLOR,
                  }}
                >
                  {takingLabel}
                </Text>
              ) : null}
            </View>

            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                lineHeight: 18,
              }}
            >
              {interaction.concern}
            </Text>

            {interaction.recommendation ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textMid,
                  lineHeight: 18,
                  fontWeight: "500",
                }}
              >
                {interaction.recommendation}
              </Text>
            ) : null}
          </Row>
        );
      })}
    </View>
  );
}
